import { Button } from '@cian/ui-kit/button';
import { FormField } from '@cian/ui-kit/form';
import { Input, MaskedInput } from '@cian/ui-kit/input';
import { ModalBody, ModalFooter } from '@cian/ui-kit/modal';
import { Select } from '@cian/ui-kit/select';
import { Textarea } from '@cian/ui-kit/textarea';
import { type FormEvent, useState } from 'react';

import type {
  IRepresentativeRequestData,
  TRepresentativeRequestType,
} from 'shared/services/createRepresentativeRequest';

import style from './style.css';
import {
  INITIAL_REPRESENTATIVE_REQUEST,
  type IRepresentativeRequestValues,
  REPRESENTATIVE_REQUEST_TYPES,
  getRepresentativeRequestData,
  hasRequestComment,
  validateRepresentativeRequest,
} from './utils';

/** Российский номер (решение пользователя) */
const PHONE_MASK = '+{7} (000) 000-00-00';

export interface IRequestFormProps {
  /** Не отклоняется: успех и ошибку показывает родитель */
  onSubmit(data: IRepresentativeRequestData): Promise<void>;
}

/**
 * Форма заявки — `BSCDialogForm` монолита. Ошибки показываются после первой попытки отправить и дальше
 * пересчитываются при вводе
 */
export const RequestForm = ({ onSubmit }: IRequestFormProps) => {
  const [values, setValues] = useState(INITIAL_REPRESENTATIVE_REQUEST);
  const [isValidated, setIsValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errors = isValidated ? validateRepresentativeRequest(values) : {};

  const setValue = <K extends keyof IRepresentativeRequestValues>(key: K, value: IRepresentativeRequestValues[K]) =>
    setValues(current => ({ ...current, [key]: value }));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsValidated(true);

    if (Object.keys(validateRepresentativeRequest(values)).length) {
      return;
    }

    setIsSubmitting(true);
    await onSubmit(getRepresentativeRequestData(values));
    setIsSubmitting(false);
  };

  return (
    <form className={style['form']} noValidate onSubmit={handleSubmit} data-name="RequestForm">
      <ModalBody>
        <div className={style['fields']}>
          <FormField label="Тип заявки">
            <Select
              value={values.requestType}
              options={REPRESENTATIVE_REQUEST_TYPES}
              onChange={value => setValue('requestType', value as TRepresentativeRequestType)}
            />
          </FormField>
          <FormField label="Имя и фамилия" required errorMessage={errors.name}>
            <Input
              autoFocus
              aria-label="Имя и фамилия"
              value={values.name}
              invalid={Boolean(errors.name)}
              onChange={(_, value) => setValue('name', value)}
            />
          </FormField>
          <FormField label="Телефон" required errorMessage={errors.phone}>
            <MaskedInput
              mask={PHONE_MASK}
              aria-label="Телефон"
              placeholder="+7 (___) ___-__-__"
              value={values.phone ? `7${values.phone}` : ''}
              invalid={Boolean(errors.phone)}
              onChange={(_, { maskedValue }) => setValue('phone', maskedValue.replace(/\D/g, '').slice(1))}
            />
          </FormField>
          <FormField label="Email" errorMessage={errors.email}>
            <Input
              aria-label="Email"
              value={values.email}
              invalid={Boolean(errors.email)}
              onChange={(_, value) => setValue('email', value)}
            />
          </FormField>
          <FormField label="Название БЦ">
            <Input
              aria-label="Название БЦ"
              value={values.buildingName}
              onChange={(_, value) => setValue('buildingName', value)}
            />
          </FormField>
          {hasRequestComment(values.requestType) && (
            <FormField label="Комментарий">
              <Textarea
                aria-label="Комментарий"
                minRows={3}
                value={values.comment}
                onChange={(_, value) => setValue('comment', value)}
              />
            </FormField>
          )}
        </div>
      </ModalBody>
      <ModalFooter divider>
        <Button theme="main_primary" size="XS" type="submit" loading={isSubmitting}>
          Отправить
        </Button>
      </ModalFooter>
    </form>
  );
};
